import { GridRowId, gridFilteredSortedRowIdsSelector, gridRowTreeSelector } from '@mui/x-data-grid-premium';
import { useCallback } from 'react';
import type { GridApiRef } from '../DataGrid';
import { isColumnEditableForRow } from './cellEditability';

export type NavDirection = 'forward' | 'backward';

type GridApi = NonNullable<GridApiRef['current']>;

/**
 * Tree nodes that are NOT an editable data row.
 *
 * `getCellParams` THROWS `MissingRowIdError` for a node that carries no row
 * model (useGridParamsApi.js:69-74) — true of every autogenerated node, e.g. a
 * group footer, which still appears in the filtered+sorted ids. So the node
 * type has to be rejected BEFORE the params lookup, never after it.
 */
const NON_DATA_ROW_TYPES: ReadonlySet<string> = new Set(['footer', 'pinnedRow', 'skeletonRow']);

/**
 * ONE editability probe for every keyboard traversal: MUI's own rule AND the
 * per-row rule, behind the two shapes that would otherwise throw.
 */
export function isGridCellEditable(api: GridApi, rowId: GridRowId, field: string): boolean {
  const rowNode = api.getRowNode(rowId);

  if (!rowNode || NON_DATA_ROW_TYPES.has(rowNode.type) || api.getRow(rowId) == null) {
    return false;
  }

  const params = api.getCellParams(rowId, field);
  // Evaluated BEFORE MUI's rule and never short-circuited past — the per-row
  // rule is a column contract, not an optimisation (adjacency.test.ts pins it).
  const columnIsEditableForRow = isColumnEditableForRow(params.colDef, params.row);

  return api.isCellEditable(params) && columnIsEditableForRow;
}

/** Filtered + sorted ids sharing `rowId`'s parent, in visual order. */
function getSiblingRowIds(apiRef: GridApiRef, rowId: GridRowId): GridRowId[] {
  const rowTree = gridRowTreeSelector(apiRef);
  const currentRowNode = rowTree[rowId];

  if (!currentRowNode) {
    return [];
  }

  // Auto-generated rows (the group footer / TOTALS row, pinned and skeleton rows) share the root
  // group as their parent, so they read as siblings of real data rows. They hold no editable cells,
  // so navigating onto one strands the roving tabindex and Tab dead-ends.
  return gridFilteredSortedRowIdsSelector(apiRef).filter((id) => {
    const node = rowTree[id];

    return (
      node?.parent === currentRowNode.parent &&
      node.type !== 'footer' &&
      node.type !== 'pinnedRow' &&
      node.type !== 'skeletonRow'
    );
  });
}

export function useGetAdjacentCell(apiRef: GridApiRef) {
  return useCallback(
    (rowId: GridRowId, field: string, direction: NavDirection) => {
      const api = apiRef.current;
      if (!api) return null;

      const rtl = direction === 'backward';
      const colIndex = api.getColumnIndex(field);
      const columns = api.getVisibleColumns();
      const nextColumns = rtl ? columns.slice(0, colIndex).reverse() : columns.slice(colIndex + 1);
      let nextColumn = null;

      for (const column of nextColumns) {
        if (isGridCellEditable(api, rowId, column.field)) {
          nextColumn = column;
          break;
        }
      }

      if (!nextColumn) {
        return null;
      }

      return nextColumn.field;
    },
    [apiRef]
  );
}

export function useGetAdjacentRow(apiRef: GridApiRef) {
  return useCallback(
    (rowId: GridRowId, direction: NavDirection) => {
      if (!apiRef.current) return undefined;

      const btt = direction === 'backward';
      const siblingRows = getSiblingRowIds(apiRef, rowId);
      const currentRowIndex = siblingRows.indexOf(rowId);

      // An active filter can hide the row the user is editing. Without this the
      // -1 walks to `siblingRows[0]` and Tab JUMPS TO THE TOP of the group.
      if (currentRowIndex === -1) {
        return undefined;
      }

      return siblingRows[currentRowIndex + (btt ? -1 : 1)];
    },
    [apiRef]
  );
}

/**
 * The next row whose `field` cell actually accepts an editor.
 *
 *   row 1  cost  editable  ◄─ Enter pressed here
 *   row 2  cost  LOCKED    ─┐
 *   row 3  cost  LOCKED    ─┴─ skipped, not landed on
 *   row 4  cost  editable  ◄─ returned
 *   (none left)            ──▶ undefined — the caller clamps
 *
 * Walks the SAME filtered+sorted sibling list as `useGetAdjacentRow`, once, so
 * Enter and Tab agree on what "the next row" is without an O(n²) re-scan per
 * skipped row.
 */
export function useGetNextEditableRow(apiRef: GridApiRef) {
  return useCallback(
    (rowId: GridRowId, field: string, direction: NavDirection): GridRowId | undefined => {
      const api = apiRef.current;
      if (!api) return undefined;

      const siblingRows = getSiblingRowIds(apiRef, rowId);
      const startIndex = siblingRows.indexOf(rowId);

      if (startIndex === -1) {
        return undefined;
      }

      const step = direction === 'backward' ? -1 : 1;

      for (let index = startIndex + step; index >= 0 && index < siblingRows.length; index += step) {
        if (isGridCellEditable(api, siblingRows[index], field)) {
          return siblingRows[index];
        }
      }

      return undefined;
    },
    [apiRef]
  );
}

export function useGetFirstEditableCell(apiRef: GridApiRef) {
  return useCallback(
    (rowId: GridRowId, direction: NavDirection) => {
      const api = apiRef.current;
      if (!api) return null;

      const rtl = direction === 'forward';
      let columns = api.getVisibleColumns();

      if (!rtl) {
        columns = [...columns].reverse();
      }

      let editableColumn = null;

      for (const column of columns) {
        if (isGridCellEditable(api, rowId, column.field)) {
          editableColumn = column;
          break;
        }
      }

      if (!editableColumn) {
        return null;
      }

      return api.getColumnIndex(editableColumn.field);
    },
    [apiRef]
  );
}
